import { QUANTITY_SCALE, compare, fromString, isPositive, round, subtract, toString, zero, } from '@aeris/money';
/**
 * Inventory.
 *
 * Master Specification section 14 states the whole design in two lines:
 *
 *   > Fonte de verdade: InventoryMovement
 *   > NÃO: product.stock = quantidade
 *
 * So stock is a ledger. `stock_movement` is append-only and is the truth;
 * `stock_balance` is a *maintained figure* that exists because summing a
 * million rows to render a list is not a query anybody wants to run. It is a
 * cache with an invariant — it is written in the same transaction as the
 * movement that changes it, under a row lock — and it can always be rebuilt
 * from the ledger. The ledger can never be rebuilt from it.
 *
 * That distinction is not pedantry. A mutable `stock` column has no answer to
 * "why is this 7?", and every stock discrepancy a shopkeeper ever reports is
 * that question.
 *
 * Quantities are `Decimal` throughout, never a JavaScript number. Section 9
 * requires decimal quantities (1,5 kg is ordinary) and ADR-0016 explains why a
 * float is not one.
 */
// ---------------------------------------------------------------------------
// Movement kinds
// ---------------------------------------------------------------------------
/**
 * What a movement was.
 *
 * A short list on purpose. Section 14 names a dozen more — RETURN, DAMAGE,
 * LOSS, BONUS, RESERVATION — and every one of them belongs to a feature that
 * does not exist yet: a return needs a sale, damage needs a reason taxonomy,
 * a reservation needs something to reserve for. Adding an unused kind now
 * would be inventing the semantics of a feature nobody has specified.
 *
 * Adding one later is `alter table … drop constraint … add constraint …` plus
 * an entry here. That is a cheap change, and a deliberately cheap one.
 */
export const MOVEMENT_KINDS = [
    /** Stock arrived: a purchase received, an opening balance, a production run. */
    'entry',
    /** Stock left, for a reason that is not a transfer. */
    'exit',
    /** A correction against a physical count. See `inventory.adjust`. */
    'adjustment',
    /** The outbound leg of a transfer between two branches. */
    'transfer_out',
    /** The inbound leg. Always written in the same transaction as its `transfer_out`. */
    'transfer_in',
];
export const isMovementKind = (value) => typeof value === 'string' &&
    MOVEMENT_KINDS.includes(value);
/**
 * Which way a movement pushes the balance.
 *
 * Stored alongside the kind rather than only derived from it, because
 * `quantity` is always positive and something has to carry the sign. A signed
 * quantity was the alternative and is worse: `-3` and `3` in one column means
 * every aggregate, every check constraint and every report has to remember
 * which one it is looking at, and `sum(quantity)` silently becomes a net
 * figure that reads like a volume.
 */
export const MOVEMENT_DIRECTIONS = ['in', 'out'];
/**
 * The direction a kind fixes, or `null` when the kind does not fix one.
 *
 * Four of the five kinds are unambiguous. `adjustment` is not, and cannot be:
 * a physical count can be higher or lower than the system, and both outcomes
 * are the same operation. So it is the one kind whose direction the caller
 * states, and a check constraint in migration 0007 pins the other four so a
 * row can never claim `transfer_out` while adding stock.
 *
 * Returning `null` rather than defaulting to `'in'` is the point: a default
 * here would let an under-count be silently recorded as an addition.
 */
export const directionOf = (kind) => {
    switch (kind) {
        case 'entry':
        case 'transfer_in':
            return 'in';
        case 'exit':
        case 'transfer_out':
            return 'out';
        case 'adjustment':
            return null;
    }
};
/**
 * What caused a movement.
 *
 * `manual` is a person typing into the inventory screen. `adjustment` is a
 * count correction. `transfer` ties the two legs of a branch transfer together
 * through a shared `sourceId`.
 *
 * `purchase` and `return` are deliberately absent: they arrive with the
 * features that emit them, each adding one value to the check constraint in
 * its own migration. Listing them now would be a schema that lies about what
 * the product can do.
 *
 * `sale` arrived that way, with F013 and migration 0010. It is what a completed
 * sale's exit movement and a cancelled sale's reversing entry both carry, so
 * `where source_type = 'sale' and source_id = …` returns the whole stock story
 * of one document — including the reversal, because a cancellation writes new
 * movements rather than deleting the originals.
 */
export const MOVEMENT_SOURCE_TYPES = [
    'manual',
    'adjustment',
    'transfer',
    'sale',
    /**
     * A receipt against a purchase (migration 0016, ADR-0020).
     *
     * The only source that carries a COST, which is what makes it the one that
     * moves `product_variant.average_cost`. Everything the shop pays for enters
     * the ledger through here.
     */
    'purchase',
    /**
     * Uma troca (migração 0069, F126).
     *
     * Nos dois sentidos, como `sale`: a entrada da peça devolvida que volta ao
     * estoque e a saída da peça que o cliente leva, com o mesmo `sourceId`. A
     * peça que "já era" não gera movimento nenhum — ela não volta ao vendável.
     */
    'exchange',
];
// ---------------------------------------------------------------------------
// Quantities
// ---------------------------------------------------------------------------
/** Zero, at the scale the database column carries. */
export const ZERO_QUANTITY = zero(QUANTITY_SCALE);
/**
 * Reads a quantity from a string.
 *
 * A string, always: NUMERIC arrives from the driver as text (see
 * `packages/database/src/client.ts`) and travels the wire as text, precisely so
 * that no boundary can round it. Returns null rather than throwing, because an
 * unparseable quantity is ordinary user input rather than a programming error.
 */
export const parseQuantity = (value) => {
    try {
        return round(fromString(value.trim()), QUANTITY_SCALE, 'half-up');
    }
    catch {
        return null;
    }
};
/** The wire and database format. Never a JSON number. */
export const quantityToString = (value) => toString(value);
/**
 * A movement quantity must be strictly positive.
 *
 * Zero is not a movement — it is a row that claims something happened when
 * nothing did, and it makes every history screen longer without making it
 * truer. Negative is rejected because `direction` carries the sign.
 */
export const isValidMovementQuantity = (value) => isPositive(value);
/**
 * Formats a quantity for a Brazilian reader: `1.234,5`.
 *
 * Trailing zeros are dropped, so a stock of three units reads `3` rather than
 * `3,000000`. Built from the exact value rather than through `Intl`, which
 * takes a `number` and would reintroduce the float this package exists to
 * avoid.
 */
export const formatQuantity = (value) => {
    const text = toString(value);
    const negative = text.startsWith('-');
    const body = negative ? text.slice(1) : text;
    const [whole = '0', fraction = ''] = body.split('.');
    const trimmed = fraction.replace(/0+$/, '');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negative ? '-' : ''}${grouped}${trimmed.length > 0 ? `,${trimmed}` : ''}`;
};
/** True when the two quantities are the same amount, whatever their scales. */
export const isSameQuantity = (left, right) => compare(left, right) === 0;
/**
 * The movement that turns a system figure into a counted one.
 *
 * Returns null when they already agree: counting a shelf and finding exactly
 * what the system said is not an event, and writing a zero-quantity
 * `adjustment` row for it would bury the counts that did find something.
 */
export const adjustmentTo = (onHand, counted) => {
    const difference = subtract(counted, onHand);
    if (difference.units === 0n)
        return null;
    return difference.units > 0n
        ? { direction: 'in', quantity: difference }
        : {
            direction: 'out',
            quantity: { units: -difference.units, scale: difference.scale },
        };
};
//# sourceMappingURL=entities.js.map